import {
  CalendarDots,
  CheckCircle,
  CircleDashed,
  PaperPlaneTilt,
  Percent,
  UserList,
} from "@phosphor-icons/react";
import { useState } from "react";

import {
  MobileSelectionListItem,
  PaginatedMobileSelectionList,
} from "common/components/MobileSelectionList";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { formatDate } from "common/helpers/utils";
import type { Campaign } from "common/types";

import { CAMPAIGN_OUTREACH_OPTIONS } from "../../../constants";
import CampaignStateTag from "../../CampaignStateTag";
import WorkerAccountsTag from "../../WorkerAccountsTag";
import CampaignPreview from "./CampaignPreview";
import ProgressTag from "./ProgressTag";
import SuccessTag from "./SuccessTag";

const MOBILE_TAGS = {
  accounts: {
    icon: <UserList weight="fill" />,
    label: "Accounts",
    renderTag: ({ owners, uses_connection_note: isUsingNote }: Campaign) => (
      <WorkerAccountsTag owners={owners} isUsingNote={isUsingNote} size="sm" />
    ),
    isDefault: true,
  },
  status: {
    icon: <CircleDashed weight="fill" />,
    label: "Status",
    renderTag: ({
      state,
      owners,
      start_date: startDate,
      uses_connection_note: isUsingNote,
    }: Campaign) => (
      <CampaignStateTag
        isUsingNote={isUsingNote}
        owners={owners}
        size="sm"
        startDate={startDate}
        state={state}
      />
    ),
    isDefault: true,
  },
  outreach_type: {
    icon: <PaperPlaneTilt weight="fill" />,
    label: "Campaign type",
    renderTag: ({ outreach_type: outreachType }: Campaign) => (
      <Tag size="sm" variant="tertiary">
        {outreachType === "drip" ? "Inbound" : "Outbound"}
      </Tag>
    ),
  },
  created_at: {
    icon: <CalendarDots weight="fill" />,
    label: "Created at",
    renderTag: ({ created_at: createdAt }: Campaign) => (
      <Tag
        size="sm"
        leftIcon={<CalendarDots weight="fill" />}
        variant="tertiary"
      >
        {formatDate(createdAt)}
      </Tag>
    ),
  },
  completion: {
    icon: <Percent weight="fill" />,
    label: "Progress",
    renderTag: ({ id, state }: Campaign) =>
      !(state === "INIT" || state === "CREATED") && (
        <ProgressTag campaignId={id} size="sm" />
      ),
  },
  success: {
    icon: <CheckCircle weight="fill" />,
    label: "Success",
    renderTag: ({ id, outreach_type: outreachType, state }: Campaign) =>
      !(state === "INIT" || state === "CREATED") && (
        <SuccessTag campaignId={id} outreachType={outreachType} size="sm" />
      ),
    isDefault: true,
  },
} as const;

interface MobileListProps {
  campaigns: Campaign[];
  selectedCampaigns: Campaign[];
  setSelectedCampaigns: (selectedCampaigns: Campaign[]) => void;
}

export default function MobileList({
  campaigns,
  selectedCampaigns,
  setSelectedCampaigns,
}: MobileListProps) {
  const { permissions } = useSelectedWorkspaceContext();
  const hasCampaignsFullPermission = permissions.includes("campaigns_full");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign>(null);

  return (
    <>
      <PaginatedMobileSelectionList
        title="Campaigns"
        data={campaigns}
        localStorageId="campaigns"
        selection={selectedCampaigns}
        onSelectionChange={
          hasCampaignsFullPermission
            ? (newSelection) => {
                setSelectedCampaigns(
                  newSelection === "all" ? campaigns : newSelection,
                );
              }
            : undefined
        }
        allItems={MOBILE_TAGS}
      >
        {(campaign, listItemProps) => {
          const { name, outreach_type: outreachType } = campaign;
          const { icon: SubtypeIcon } = CAMPAIGN_OUTREACH_OPTIONS[outreachType];

          return (
            <MobileSelectionListItem
              onClick={() => {
                setSelectedCampaign(campaign);
                setIsPreviewOpen(true);
              }}
              {...listItemProps}
            >
              <div className="flex flex-col items-start">
                <h3 className="text-xs-bold text-left line-clamp-1">{name}</h3>

                <div className="flex items-center gap-x-1 text-black-500">
                  <SubtypeIcon size={16} />
                  <span className="line-clamp-1 break-all text-button-xs">
                    {outreachType === "drip" ? "1st" : "2nd & 3rd+"} connections
                  </span>
                </div>
              </div>
            </MobileSelectionListItem>
          );
        }}
      </PaginatedMobileSelectionList>

      {selectedCampaign && (
        <CampaignPreview
          isOpen={isPreviewOpen}
          setIsOpen={setIsPreviewOpen}
          campaign={selectedCampaign}
        />
      )}
    </>
  );
}
